import { Link2 } from 'lucide-react';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import {
  FormDateField,
  FormDialog,
  FormSection,
  FormStatusField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import {
  MAX_ACTIVE_WEB_URLS,
  WEB_URL_DESCRIPTION_MAX_LENGTH,
  WEB_URL_TITLE_MAX_LENGTH,
} from '../constants/webUrl.constants';
import { useWebUrlForm } from '../hooks/useWebUrlForm';
import type { WebUrl } from '../types/webUrl.types';

interface WebUrlFormModalProps {
  webUrl: WebUrl | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WebUrlFormModal({ webUrl, open, onOpenChange }: WebUrlFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useWebUrlForm({
    webUrl,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={handleOpenChange}
        icon={Link2}
        title={isEditMode ? 'Edit Web URL' : 'Add Web URL'}
        description="A link shown in the mobile app slider."
        form={form}
        isPending={isPending}
        isEditMode={isEditMode}
        onSubmit={submit}
      >
        <FormSection>
          <FormTextField
            control={form.control}
            name="title"
            label="Title"
            placeholder="Enter a title"
            maxLength={WEB_URL_TITLE_MAX_LENGTH}
            hasCharacterCount
            isRequired
          />
          <FormTextField
            control={form.control}
            name="actionLink"
            label="Link"
            placeholder="https://example.com"
            isRequired
          />
          <FormDateField control={form.control} name="activeFrom" label="Active From" isRequired />
          <FormDateField control={form.control} name="activeTo" label="Active To" isRequired />
          <FormTextareaField
            control={form.control}
            name="description"
            label="Description"
            placeholder="Shown with the link"
            maxLength={WEB_URL_DESCRIPTION_MAX_LENGTH}
            hasCharacterCount
            isFullRow
          />
        </FormSection>
        <FormStatusField
          control={form.control}
          name="isActive"
          description={`At most ${MAX_ACTIVE_WEB_URLS} links can be active at the same time.`}
        />
      </FormDialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
